import {
  CurrentUserSavedActivitiesQuery,
  SaveActivityMutation,
  SaveActivityMutationVariables,
  SaveToCollectionsMutation,
  SaveToCollectionsMutationVariables,
  UnsaveActivityMutation,
  UnsaveActivityMutationVariables,
  UnsaveToCollectionsMutation,
  UnsaveToCollectionsMutationVariables,
} from "@outschool/gql-frontend-generated";
import { useAnalytics, usePageContext } from "@outschool/ui-analytics";
import { ExecutionResult, gql, useMutation } from "@outschool/ui-apollo";
import { dataIdFromObject } from "@outschool/ui-gql";
import { validOptimisticResponse } from "@outschool/ui-utils";
import React from "react";

import { SavedActivityFragment } from "./fragments";
import { CurrentUserSavedActivitiesQueryStr } from "./queries";

export function useSavedActivityMutations(activityUid: string) {
  const saveActivity = useSaveActivityMutation(activityUid);
  const unsaveActivity = useUnsaveActivityMutation(activityUid);

  return { saveActivity, unsaveActivity };
}

export const saveActivityMutation = gql`
  mutation SaveActivity(
    $activityUid: ID!
    $referer: String
    $attribution: Attribution
  ) {
    saveActivity(
      activityUid: $activityUid
      referer: $referer
      attribution: $attribution
    ) {
      ...SavedActivityFragment
    }
  }
  ${SavedActivityFragment}
`;

function useSaveActivityMutation(
  activityUid: string,
): [
  (
    referer: any,
    attribution: any,
  ) => Promise<ExecutionResult<SaveActivityMutation>>,
  SaveActivityMutation["saveActivity"],
] {
  const [mutate, mutationResult] = useMutation<
    SaveActivityMutation,
    SaveActivityMutationVariables
  >(saveActivityMutation);
  const response = mutationResult.data?.saveActivity ?? null;
  const wrapped = React.useCallback(
    (referer: any, attribution: any) =>
      mutate({
        variables: {
          activityUid,
          referer,
          attribution,
        },
        optimisticResponse: validOptimisticResponse({
          __typename: "Mutation",
          saveActivity: {
            __typename: "SavedActivity",
            uid: new Date(),
            activity_uid: activityUid,
            created_at: new Date(),
          },
        }),
        // @ts-ignore TS(2339): Property 'saveActivity' does not exist on type 'Sa... Remove this comment to see the full error message
        update(store, { data: { saveActivity } }) {
          const data = store.readQuery<CurrentUserSavedActivitiesQuery>({
            query: CurrentUserSavedActivitiesQueryStr,
          });
          if (!data?.currentUser) {
            return;
          }
          const savedActivities = [
            ...data.currentUser.savedActivities,
            saveActivity,
          ];
          store.writeQuery({
            query: CurrentUserSavedActivitiesQueryStr,
            data: {
              ...data,
              currentUser: { ...data.currentUser, savedActivities },
            },
          });
        },
      }),
    [mutate, activityUid],
  );
  // @ts-ignore TS(2322): Type '{ __typename?: "SavedActivity" | undefined; ... Remove this comment to see the full error message
  return [wrapped, response];
}

export const unsaveActivityMutation = gql`
  mutation UnsaveActivity($activityUid: ID!) {
    unsaveActivity(activityUid: $activityUid)
  }
`;

function useUnsaveActivityMutation(
  activityUid: string,
): [() => Promise<ExecutionResult<UnsaveActivityMutation>>, boolean] {
  const [mutate, mutationResult] = useMutation<
    UnsaveActivityMutation,
    UnsaveActivityMutationVariables
  >(unsaveActivityMutation);
  const response = mutationResult.data?.unsaveActivity ?? false;
  const wrapped = React.useCallback(
    () =>
      mutate({
        variables: {
          activityUid,
        },
        optimisticResponse: validOptimisticResponse({
          __typename: "Mutation",
          unsaveActivity: true,
        }),
        update(store) {
          const data = store.readQuery<CurrentUserSavedActivitiesQuery>({
            query: CurrentUserSavedActivitiesQueryStr,
          });
          if (!data?.currentUser?.savedActivities) {
            return;
          }
          const savedActivities = data.currentUser.savedActivities.filter(
            sa => sa.activity_uid !== activityUid,
          );
          store.writeQuery({
            query: CurrentUserSavedActivitiesQueryStr,
            data: {
              ...data,
              currentUser: { ...data.currentUser, savedActivities },
            },
          });
        },
      }),
    [mutate, activityUid],
  );
  return [wrapped, response];
}

const SaveToCollections = gql`
  mutation SaveToCollections(
    $activityUid: ID!
    $referer: String
    $attribution: Attribution
    $savedClassListUids: [ID!]!
  ) {
    saveActivity(
      activityUid: $activityUid
      referer: $referer
      attribution: $attribution
      savedClassListUids: $savedClassListUids
    ) {
      uid
    }
  }
`;

const UnsaveToCollections = gql`
  mutation UnsaveToCollections($activityUid: ID!, $savedClassListUids: [ID!]!) {
    unsaveActivity(
      activityUid: $activityUid
      savedClassListUids: $savedClassListUids
    )
  }
`;

const RenameSavedClassList = gql`
  mutation RenameSavedClassList($uid: ID!, $name: String!) {
    renameSavedClassList(uid: $uid, name: $name)
  }
`;

const DeleteSavedClassList = gql`
  mutation DeleteSavedClassList($uid: ID!) {
    deleteSavedClassList(uid: $uid)
  }
`;

export function useSaveToCollections(activityUid: string) {
  const analytics = useAnalytics();
  const pageContext = usePageContext();
  const [saveToCollection] = useMutation<
    SaveToCollectionsMutation,
    SaveToCollectionsMutationVariables
  >(SaveToCollections);
  return React.useCallback(
    async (savedClassListUids: string[]) => {
      if (savedClassListUids.length === 0) {
        return;
      }
      const attribution = await analytics.attribution();
      await saveToCollection({
        variables: {
          activityUid,
          referer: pageContext.pagePath,
          attribution,
          savedClassListUids,
        },
        update: proxy => {
          const lastAddedToAt = new Date();
          savedClassListUids.map(uid => {
            proxy.writeFragment({
              // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
              id: dataIdFromObject({
                __typename: "SavedClassList",
                uid,
              }),
              fragment: gql`
                fragment UpdateLastAddedToAt on SavedClassList {
                  uid
                  lastAddedToAt
                }
              `,
              data: {
                __typename: "SavedClassList",
                uid,
                lastAddedToAt,
              },
            });
          });
        },
        refetchQueries: ["SavedClassListQuery"],
      });
    },
    [activityUid, saveToCollection, analytics, pageContext.pagePath],
  );
}

export function useUnsaveToCollections(activityUid: string) {
  const [unsaveToCollection] = useMutation<
    UnsaveToCollectionsMutation,
    UnsaveToCollectionsMutationVariables
  >(UnsaveToCollections);
  return React.useCallback(
    async (savedClassListUids: string[]) => {
      if (savedClassListUids.length === 0) {
        return;
      }
      await unsaveToCollection({
        variables: {
          activityUid,
          savedClassListUids,
        },
        refetchQueries: ["SavedClassListQuery"],
      });
    },
    [activityUid, unsaveToCollection],
  );
}

export function useRenameSavedClassList(savedClassListUid: string) {
  const [renameSavedClassList, { loading }] = useMutation(RenameSavedClassList);
  const rename = React.useCallback(
    async (name: string) => {
      name = name.trim();
      return await renameSavedClassList({
        variables: {
          uid: savedClassListUid,
          name,
        },
        update: proxy => {
          proxy.writeFragment({
            // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
            id: dataIdFromObject({
              __typename: "SavedClassList",
              uid: savedClassListUid,
            }),
            fragment: gql`
              fragment RenameSavedClassList on SavedClassList {
                name
              }
            `,
            data: {
              __typename: "SavedClassList",
              name,
            },
          });
        },
      });
    },
    [savedClassListUid, renameSavedClassList],
  );
  return { rename, loading };
}

export function useDeleteSavedClassList(savedClassListUid: string) {
  const [deleteSavedClassList, { loading }] = useMutation(
    DeleteSavedClassList,
    {
      variables: {
        uid: savedClassListUid,
      },
      refetchQueries: [
        "SavedClassListQuery",
        "ListCurrentUserSavedClassLists",
        "SavedClassListAndCurrentUserSavedClassLists",
      ],
    },
  );
  return { delete: deleteSavedClassList, loading };
}
